import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import {
  OIG_ADDON_BLOCKS,
  OIG_CUSTOM_BLOCK_MAILTO,
  OIG_INCLUDED_PER_FACILITY_COPY,
  createCheckoutSession,
} from "@services/payment";
import { resolveOigChecksAddonId } from "@services/companyAddons";
import { buildBillingAddonPurchaseSuccessUrl } from "@utils/billingAddonUrls";
import { getCurrentUserId } from "@utils/userUtils";

export type OigCheckBlockPickerProps = {
  open: boolean;
  onClose: () => void;
};

export default function OigCheckBlockPicker({
  open,
  onClose,
}: OigCheckBlockPickerProps) {
  const [buyingPlanId, setBuyingPlanId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [oigAddonId, setOigAddonId] = useState<string>();

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    resolveOigChecksAddonId()
      .then((addonId) => {
        if (!cancelled) setOigAddonId(addonId);
      })
      .catch(() => {
        if (!cancelled) setOigAddonId(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const buy = async (planId: number) => {
    setError(null);
    setBuyingPlanId(planId);
    try {
      const addonId = oigAddonId || (await resolveOigChecksAddonId());
      if (!addonId) {
        throw new Error("OIG Checks add-on is not available for your account.");
      }
      const successUrl = buildBillingAddonPurchaseSuccessUrl(addonId);
      const cancelParams = new URLSearchParams({
        tab: "addons",
        addon: addonId,
        step: "1",
      });
      const cancelUrl = `${window.location.origin}/dashboard/billing?${cancelParams.toString()}`;
      const data = await createCheckoutSession({
        pricing_tier_id: planId,
        metadata: {
          user_id: getCurrentUserId(),
          is_addon: true,
        },
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      const checkoutUrl = data.result?.checkout_url;
      if (!checkoutUrl) {
        throw new Error("Checkout URL was not returned");
      }
      window.location.assign(checkoutUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start checkout");
      setBuyingPlanId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid="oig-check-block-picker"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Buy exclusion-check blocks</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2, color: "#475569", fontSize: "14px" }}>
          {OIG_INCLUDED_PER_FACILITY_COPY} Company-wide monthly pool. Blocks
          stack on top of included checks and any previous add-ons. Unused
          checks do not roll over.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          {OIG_ADDON_BLOCKS.map((block) => (
            <Box
              key={block.planId}
              data-testid={`oig-addon-block-${block.checks}`}
              sx={{
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography sx={{ fontWeight: 700, color: "#155e75" }}>
                +{block.checks.toLocaleString()} / month
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                ${block.priceUsd.toLocaleString()}/yr
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
                Best fit: {block.bestFit}
              </Typography>
              <Button
                variant="contained"
                disabled={buyingPlanId !== null}
                onClick={() => buy(block.planId)}
              >
                {buyingPlanId === block.planId ? "Starting…" : "Buy"}
              </Button>
            </Box>
          ))}
        </Box>
        <Typography sx={{ mt: 2, fontSize: "13px" }}>
          Need more than 1,000 extra checks?{" "}
          <Link
            href={OIG_CUSTOM_BLOCK_MAILTO}
            data-testid="oig-addon-custom-mailto"
          >
            contact@unicomply.ai
          </Link>
        </Typography>
        {error ? (
          <Typography color="error" sx={{ mt: 1, fontSize: "13px" }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
